import * as React from "react";
import { useEffect, useRef, useState } from "react";
import { Button, Grid, HStack } from "@chakra-ui/react";
import {
  SCOPE_SUB_TYPES,
  methodologyForSubType,
  scopeHasSubTypeMethodology,
} from "utils/methodologySubKeys";
import useEsgAdminFeaturesVisible from "hooks/useEsgAdminFeaturesVisible";
import AiIngestHistory from "./AiIngestHistory";
import MethodologyCard from "./MethodologyCard";
import SourcesCard from "./SourcesCard";

// Two-column "page-fabric" block — methodology on the left (1.4fr) and
// sources on the right (1fr). Both cards share a single open/closed state:
// toggling either header expands or collapses both at once. Default closed
// so the data-entry table stays the page focus. When opened, the block
// scrolls itself into view so the user can see the full panel without a
// manual scroll.
function MethodologyAndSources({
  methodologyContent,
  onSaveMethodology,
  isMethodologySaving,
  fileInfoList,
  onFileInfoListChange,
  folder,
  readOnly = false,
  // Both optional: the ingest history renders nothing without them, and nothing without runs in this
  // scope — which is every task that has not used AI mapping, and every Scope 3 category card.
  questionnaireId,
  scopeKey,
  // Per-type methodology (Scope 1's Stationary / Mobile / Refrigerants): the whole methodology map and a
  // save keyed by the type's own key. When the scope has no sub-types these are ignored and the single
  // `methodologyContent` / `onSaveMethodology` pair is used, exactly as before.
  methodologyMap,
  onSaveSubTypeMethodology,
}) {
  const [isOpen, setIsOpen] = useState(false);
  const subTypes = SCOPE_SUB_TYPES[scopeKey] || null;
  const [activeType, setActiveType] = useState(subTypes ? subTypes[0].key : null);
  const containerRef = useRef(null);
  const showAdminFeatures = useEsgAdminFeaturesVisible();
  const toggle = () => setIsOpen((v) => !v);

  // Default to open if the user lands on a task that already has either
  // methodology content saved or sources attached — that surfaces the
  // existing data rather than hiding it behind a closed accordion. Both
  // values are loaded asynchronously by the parent, so we watch for the
  // first non-empty arrival rather than checking on mount only. After the
  // user has interacted (toggled) once, we stop overriding.
  const hasAutoOpenedRef = useRef(false);
  useEffect(() => {
    if (hasAutoOpenedRef.current) return;
    const hasContent =
      (subTypes
        ? scopeHasSubTypeMethodology(methodologyMap, scopeKey)
        : typeof methodologyContent === "string" && methodologyContent.trim().length > 0)
      || (Array.isArray(fileInfoList) && fileInfoList.length > 0);
    if (hasContent) {
      setIsOpen(true);
      hasAutoOpenedRef.current = true;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [methodologyContent, fileInfoList, methodologyMap]);

  // After the Collapse finishes opening, scroll the panel into view. We use
  // the browser's own scrollIntoView with `block: "end"` and a CSS
  // scroll-margin-bottom matching the sticky footer height — that way the
  // browser computes the exact final scroll offset itself, avoiding flaky
  // mid-animation measurements. We re-measure the footer right before
  // calling so it's accurate even when the footer has 2 vs 3 buttons.
  useEffect(() => {
    if (!isOpen) return;
    const id = setTimeout(() => {
      const el = containerRef.current;
      if (!el) return;
      const footer = document.querySelector("[data-sticky-footer]");
      const footerHeight = footer ? footer.getBoundingClientRect().height : 200;
      el.style.scrollMarginBottom = `${footerHeight + 16}px`;
      el.scrollIntoView({ behavior: "smooth", block: "end" });
    }, 320);
    return () => clearTimeout(id);
  }, [isOpen]);

  // In a finalised (read-only) task we hide cards that have no user content.
  // Empty placeholders aren't useful on a finalised view — they imply data
  // is being requested when none was ever provided.
  const safeFileList = Array.isArray(fileInfoList) ? fileInfoList : [];
  const methodologyHasContent = subTypes
    ? scopeHasSubTypeMethodology(methodologyMap, scopeKey)
    : typeof methodologyContent === "string" && methodologyContent.trim().length > 0;
  const sourcesHaveFiles = safeFileList.length > 0;
  const showMethodology = !readOnly || methodologyHasContent;
  const showSources = !readOnly || sourcesHaveFiles;

  if (!showMethodology && !showSources) return null;

  // Match the column ratio to whichever cards are actually rendered so a
  // single visible card spans full width in a finalised view.
  const templateColumns = (() => {
    if (showMethodology && showSources) return { base: "1fr", md: "1.4fr 1fr" };
    return "1fr";
  })();

  return (
    <Grid
      ref={containerRef}
      templateColumns={templateColumns}
      gap="18px"
      alignItems="start"
      mb={4}
    >
      {showMethodology && (
        <MethodologyCard
          // Keyed per type so each tab gets its own edit/read state rather than sharing one editor.
          key={activeType || "single"}
          initialContent={
            subTypes
              ? methodologyForSubType(
                  methodologyMap,
                  scopeKey,
                  subTypes.find((type) => type.key === activeType),
                  activeType === subTypes[0].key
                )
              : methodologyContent
          }
          onSave={subTypes ? (content) => onSaveSubTypeMethodology?.(activeType, content) : onSaveMethodology}
          isSaving={isMethodologySaving}
          readOnly={readOnly}
          isOpen={isOpen}
          onToggle={toggle}
          anyContent={subTypes ? methodologyHasContent : undefined}
          tabs={
            subTypes ? (
              <SubTypeTabs
                types={subTypes}
                active={activeType}
                onChange={setActiveType}
                methodologyMap={methodologyMap}
                scopeKey={scopeKey}
              />
            ) : null
          }
          // Inside the methodology card rather than beside it: the card already carries the two-line
          // "Full derivation: ai-ingest run #N" pointer, and the thing it points at belongs next to it.
          // ESG-Admin only (and honours the header's hide toggle): each entry opens the raw ingest trail.
          footer={
            showAdminFeatures ? (
              <AiIngestHistory questionnaireId={questionnaireId} scopeKey={scopeKey} />
            ) : null
          }
        />
      )}
      {showSources && (
        <SourcesCard
          fileInfoList={safeFileList}
          onFileInfoListChange={onFileInfoListChange}
          folder={folder}
          readOnly={readOnly}
          isOpen={isOpen}
          onToggle={toggle}
        />
      )}
    </Grid>
  );
}

/**
 * One tab per type, in the platform's primary tab style (lime underline). A small dot marks a type that
 * already has methodology, so the empty ones are visible without opening each tab.
 */
function SubTypeTabs({ types, active, onChange, methodologyMap, scopeKey }) {
  return (
    <HStack spacing={0} borderBottom="1px solid" borderColor="gray.200" mb="14px">
      {types.map((type, index) => {
        const isActive = type.key === active;
        const filled = /\S/.test(
          (methodologyForSubType(methodologyMap, scopeKey, type, index === 0) || "").replace(/<[^>]*>/g, "")
        );
        return (
          <Button
            key={type.key}
            variant="unstyled"
            h="auto"
            px={3}
            py={2}
            mb="-1px"
            borderRadius={0}
            fontSize="13px"
            fontWeight="600"
            color={isActive ? "darkTeal" : "gray.500"}
            borderBottom="2px solid"
            borderColor={isActive ? "lime" : "transparent"}
            _hover={{ color: "darkTeal" }}
            onClick={() => onChange(type.key)}
            aria-pressed={isActive}
          >
            {type.label}
            {filled ? (
              <span
                aria-hidden
                style={{
                  display: "inline-block",
                  width: 6,
                  height: 6,
                  marginLeft: 6,
                  borderRadius: "50%",
                  background: "var(--chakra-colors-limeShade-600)",
                  verticalAlign: "middle",
                }}
              />
            ) : null}
          </Button>
        );
      })}
    </HStack>
  );
}

export default MethodologyAndSources;
